import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useApi, useAuth, useCompany, useLocalStorage } from '@/hooks'
import { Company } from '@/types/dashboard-types'

import type { ColumnSnapshot } from '../MapContext'
import { defaultSidebarState, useMapContext } from '../MapContext'
import { StatusColumn } from '../types'

export const ASSET_COL_WIDTH_KEY = 'assetTableAssetColWidth'

interface StatusColumnsContextType {
  statusColumns: StatusColumn[]
  setStatusColumns: React.Dispatch<React.SetStateAction<StatusColumn[]>>
  isLoadingColumns: boolean
  reloadColumns: () => Promise<void>
  resetColumns: () => Promise<void>
  assetColWidth: number | null
  setAssetColWidth: (value: number | null | ((prev: number | null) => number | null)) => void
}

const StatusColumnsContext = createContext<StatusColumnsContextType | undefined>(undefined)

export const VSP_ACCESS_TYPE = {
  Disabled: 1,
  NoReeferInterfaceOnly: 2,
  ReeferAndReeferInterface: 3,
}
const applyColumnSnapshot = (base: StatusColumn[], snapshot: ColumnSnapshot): StatusColumn[] => {
  const byName = new Map(snapshot.map((s) => [s.selectedColumnsDatabaseName, s]))
  return base.map((c) => {
    const s = byName.get(c.selectedColumnsDatabaseName)
    return s ? { ...c, selected: s.selected, displayOrder: s.displayOrder, width: s.width } : c
  })
}

export const StatusColumnsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { api } = useApi()
  const { companies } = useCompany()
  const { user } = useAuth()
  const { filters, selectedSavedMap, setSidebarState } = useMapContext()
  const [statusColumns, setStatusColumns] = useState<StatusColumn[]>([])
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>()
  const [isLoadingColumns, setIsLoadingColumns] = useState(false)
  const [assetColWidth, setAssetColWidth] = useLocalStorage<number | null>(ASSET_COL_WIDTH_KEY, null)

  // upon company id filter change set selected company
  useEffect(() => {
    if (!filters.companyId) {
      setSelectedCompany(undefined)
    } else {
      const slectedCompany = companies.find((c) => c.id === filters.companyId)
      setSelectedCompany(slectedCompany)
    }
  }, [filters.companyId, companies])

  const loadColumns = useCallback(async () => {
    if (!api || !selectedCompany?.id || !user?.refreshUserId) {
      return
    }
    setIsLoadingColumns(true)
    try {
      const columns = await api.simpleGet<StatusColumn[]>({
        path: `assets/status-columns?companyId=${selectedCompany.id}`,
        origin: 'StatusColumnsSelector',
      })
      const shortAddressColumn = columns.find((c) => c.selectedColumnsDisplayName === 'Short Address')
      if (shortAddressColumn) {
        shortAddressColumn.selectedColumnsDisplayName = 'Address'
      }

      // fix virtual setpoint columns
      const displayColumns = columns.filter((c) => {
        if (
          selectedCompany.virtualSetpointAccessTypeId === VSP_ACCESS_TYPE.Disabled &&
          c.selectedColumnsDatabaseName.endsWith('VirtualSetpoint')
        ) {
          return false
        }
        return true
      })
      const snapshot = selectedSavedMap?.columns
      const final = snapshot?.length ? applyColumnSnapshot(displayColumns, snapshot) : displayColumns

      setStatusColumns(final)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoadingColumns(false)
    }
  }, [api, selectedCompany, user, selectedSavedMap])

  useEffect(() => {
    loadColumns()
  }, [loadColumns])

  // Reset all list column settings (selection, order, widths) back to the defaults: deselect every
  // data column, leaving just the asset column.
  const resetColumns = useCallback(async () => {
    setAssetColWidth(null)
    // Reset the list/map split back to the lean default so the map gets more space.
    setSidebarState((prev) => ({
      ...prev,
      mapPanelWidth: defaultSidebarState.mapPanelWidth,
      splitRatio: defaultSidebarState.splitRatio,
    }))

    // In a saved view the view — not the user's profile — is the target. Reset the in-memory
    // working copy only; it shows immediately and is persisted when the user hits Save. Writing to
    // the profile here would do nothing visible, because the view's column snapshot is re-applied
    // over the profile on every reload.
    if (selectedSavedMap) {
      setStatusColumns((prev) => prev.map((c) => ({ ...c, selected: 0, displayOrder: 0, width: null })))
      return
    }

    if (!api || !selectedCompany?.id) {
      return
    }
    const removed = statusColumns.filter((c) => c.selected === 1).map((c) => c.selectedColumnsDatabaseName)
    await api.put({
      path: `assets/status-columns`,
      origin: 'StatusColumnsSelector',
      body: { added: [], removed },
    })
    await loadColumns()
  }, [selectedSavedMap, api, selectedCompany, statusColumns, loadColumns, setAssetColWidth, setSidebarState])

  const value = useMemo(
    () => ({
      statusColumns,
      setStatusColumns,
      isLoadingColumns,
      reloadColumns: loadColumns,
      resetColumns,
      assetColWidth,
      setAssetColWidth,
    }),
    [statusColumns, isLoadingColumns, loadColumns, resetColumns, assetColWidth, setAssetColWidth]
  )

  return <StatusColumnsContext.Provider value={value}>{children}</StatusColumnsContext.Provider>
}

export const useStatusColumnsContext = () => {
  const ctx = useContext(StatusColumnsContext)
  if (!ctx) {
    throw new Error('useStatusColumnsContext must be used within StatusColumnsProvider')
  }
  return ctx
}
